import React from 'react';
import { Link } from 'react-router-dom';
import { Button, Section, SectionHeader } from '../../design-system';
import MarketingLayout from '../layouts/MarketingLayout';
import ConversationalDemo from '../components/ConversationalDemo';
import BeforeAfterDemo from '../components/BeforeAfterDemo';
import FadeIn from '../components/FadeIn';
import ScreenPreview from '../components/ScreenPreview';
import LiveNetworkSection from '../components/LiveNetworkSection';
import ProofStatsBar from '../components/ProofStatsBar';
import ProofPhotoWall from '../components/ProofPhotoWall';
import { EXAMPLE_PROMPTS, AI_FEATURES, APPLICATIONS } from '../data/content';
import coverVideo from '../../assets/images/cover.mp4';
import SeoHead from '../seo/SeoHead';
import { HOME_SEO, buildOrganizationJsonLd } from '../seo/pages';

const SCREEN_COUNTS = [1, 5, 20, 100];

function TvIcon({ className = 'w-5 h-5' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="2" y="4" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.8" />
      <path d="M8 21h8M12 18v3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function MegaphoneIcon({ className = 'w-5 h-5' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M3 11v2a2 2 0 0 0 2 2h1l4 4V5L6 9H5a2 2 0 0 0-2 2Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path d="M15 9.5a4.5 4.5 0 0 1 0 5M17.5 7a8 8 0 0 1 0 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

export default function Home() {
  return (
    <MarketingLayout>
      <SeoHead
        title={HOME_SEO.title}
        description={HOME_SEO.description}
        path={HOME_SEO.path}
        jsonLd={buildOrganizationJsonLd()}
      />
      {/* Hero — centered, high-impact first viewport */}
      <section className="relative min-h-[100svh] bg-invert-navy text-white overflow-hidden flex flex-col">
        <div className="absolute inset-0 bg-hero-gradient" />
        <div className="absolute inset-0 bg-dot-grid opacity-40 [background-size:28px_28px]" />
        <div className="absolute inset-0 bg-glow-orange pointer-events-none" />
        <div className="absolute inset-0 bg-hero-vignette pointer-events-none" />

        <div className="relative flex-1 flex items-center justify-center px-6 lg:px-8 pt-24 pb-16 lg:pt-28 lg:pb-20">
          <div className="text-center max-w-4xl mx-auto w-full">
            <p className="font-interblack text-xl sm:text-2xl tracking-[0.2em] text-white mb-8">
              INVERT
            </p>
            <h1 className="font-interblack text-[2.4rem] leading-[1.05] sm:text-5xl md:text-6xl lg:text-7xl xl:text-[5rem] tracking-tight text-white mb-7">
              Talk to your TV,{' '}
              <span className="text-invert-orange">display what you want.</span>
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-slate-300 leading-relaxed max-w-xl mx-auto mb-11">
              Restaurant TV content for menus and specials — plus a restaurant TV box that updates every screen when you type.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <Link
                to="/screenHostSignup"
                className="inline-flex items-center justify-center gap-2.5 min-w-[180px] px-8 py-3.5 rounded-xl bg-invert-orange text-white font-semibold text-base shadow-lg shadow-invert-orange/25 hover:bg-invert-orange-deep transition-all duration-200 no-underline"
              >
                <TvIcon />
                Venues
              </Link>
              <Link
                to="/advertise"
                className="inline-flex items-center justify-center gap-2.5 min-w-[180px] px-8 py-3.5 rounded-xl bg-white/5 text-white font-semibold text-base border border-white/20 hover:bg-white/10 hover:border-white/35 transition-all duration-200 no-underline backdrop-blur-sm"
              >
                <MegaphoneIcon />
                Advertisers
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Live network / gallery fallback */}
      <Section className="bg-slate-50 !py-12 lg:!py-16">
        <LiveNetworkSection compact showStatusLink />
      </Section>

      {/* Just Type */}
      <Section id="demo">
        <SectionHeader
          label="Just Type"
          title="Businesses simply think. INVERT designs."
          subtitle="One sentence is all it takes. INVERT handles typography, animations, brand colors, icons, scheduling, and screen formatting — automatically."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {EXAMPLE_PROMPTS.map((ex, i) => (
            <FadeIn key={ex.prompt} delay={i * 0.08}>
              <div className="group rounded-2xl border border-invert-border bg-white p-6 hover:shadow-lg hover:border-invert-orange/30 transition-all duration-300 h-full">
                <p className="text-sm text-invert-orange font-medium mb-3">Type:</p>
                <p className="text-lg font-semibold text-invert-navy mb-4">
                  &ldquo;{ex.prompt}&rdquo;
                </p>
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-3">Instantly generates:</p>
                <ul className="space-y-1.5">
                  {ex.generates.map((g) => (
                    <li key={g} className="text-sm text-slate-600 flex items-center gap-2">
                      <span className="text-invert-orange">•</span> {g}
                    </li>
                  ))}
                </ul>
                <div className="mt-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300 scale-90 group-hover:scale-100">
                  <ScreenPreview theme={ex.theme} animate={false} className="max-w-[200px] mx-auto" />
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
      </Section>

      {/* Before / After */}
      <Section className="bg-slate-50">
        <SectionHeader
          label="The Difference"
          title="Never design another promotion again"
          subtitle="Stop opening Canva. Stop hiring designers. Stop uploading slides."
        />
        <BeforeAfterDemo />
      </Section>

      {/* Applications */}
      <Section>
        <SectionHeader
          label="Platform Applications"
          title="One platform. Every use case."
          subtitle="From daily specials to hiring announcements — and yes, advertising too."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {APPLICATIONS.map((app, i) => (
            <FadeIn key={app.title} delay={i * 0.05}>
              <div
                className={`rounded-xl p-5 border transition-all hover:shadow-md ${
                  app.highlight
                    ? 'border-invert-orange/40 bg-invert-orange/5'
                    : 'border-invert-border bg-white hover:border-invert-orange/20'
                }`}
              >
                <h3 className="font-semibold text-invert-navy mb-1">{app.title}</h3>
                <p className="text-sm text-slate-500">&ldquo;{app.prompt}&rdquo;</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </Section>

      {/* AI Features */}
      <Section dark>
        <SectionHeader
          label="AI Features"
          title="Intelligence built into every screen"
          subtitle="Futuristic capabilities that feel like magic — because they are."
          dark
        />
        <div className="grid sm:grid-cols-2 gap-6 lg:gap-8">
          {AI_FEATURES.map((feat, i) => (
            <FadeIn key={feat.title} delay={i * 0.1}>
              <div className="glass-card rounded-2xl p-8 h-full">
                <span className="text-2xl text-invert-orange mb-4 block">{feat.icon}</span>
                <h3 className="text-xl font-semibold text-white mb-3">{feat.title}</h3>
                <p className="text-slate-300 leading-relaxed">{feat.description}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </Section>

      {/* Conversational AI */}
      <Section className="bg-slate-50">
        <SectionHeader
          label="Signature Feature"
          title="Your AI marketing assistant"
          subtitle="Not just a content generator — a conversational partner that understands your business."
        />
        <ConversationalDemo />
      </Section>

      {/* Screen Network */}
      <Section>
        <SectionHeader
          label="Screen Network"
          title="Type once. Update every screen."
          subtitle="Whether you have 1 TV or 100 — one prompt updates them all instantly."
        />
        <div className="flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-16">
          <div className="flex items-end gap-3 lg:gap-4">
            {SCREEN_COUNTS.map((count, i) => (
              <FadeIn key={count} delay={i * 0.15}>
                <div className="text-center">
                  <div
                    className="bg-invert-navy rounded-lg mb-2 mx-auto"
                    style={{
                      width: `${24 + i * 12}px`,
                      height: `${16 + i * 10}px`,
                    }}
                  />
                  <span className="text-2xl lg:text-3xl font-bold text-invert-navy">{count}</span>
                  <p className="text-xs text-slate-400">TV{count > 1 ? 's' : ''}</p>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={0.3} className="max-w-md text-center lg:text-left">
            <p className="text-lg text-slate-600 leading-relaxed">
              Install INVERT on every screen in your business. Type a promotion once — it appears everywhere, perfectly formatted for each display.
            </p>
          </FadeIn>
        </div>
      </Section>

      {/* Monetize */}
      <Section dark className="relative overflow-hidden">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <FadeIn>
            <p className="text-xs font-semibold uppercase tracking-widest text-invert-orange mb-4">
              Monetize Unused Time
            </p>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6 leading-tight">
              Earn passive income when you&apos;re not promoting
            </h2>
            <p className="text-slate-300 text-lg leading-relaxed mb-6">
              When your screens aren&apos;t displaying your own content, INVERT intelligently fills available time with local advertising, community announcements, and revenue-generating campaigns.
            </p>
            <p className="text-slate-400">
              Advertising is a bonus — not the reason to install. Your screens work for you 24/7.
            </p>
            <Button to="/screenHostSignup" className="mt-8">
              Start Earning
            </Button>
          </FadeIn>
          <FadeIn delay={0.2}>
            <div className="relative rounded-2xl overflow-hidden aspect-video">
              <video
                src={coverVideo}
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-full object-cover"
              />
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Installation shots */}
      <Section>
        <SectionHeader
          label="Real Screens"
          title="Trusted by restaurants and venues"
          subtitle="Real INVERT restaurant TV content running in restaurants, retail, and neighborhood businesses."
        />
        <ProofStatsBar className="mb-10" />
        <ProofPhotoWall />
        <div className="text-center mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button to="/restaurant-tv" variant="secondary">
            Restaurant TV Content
          </Button>
          <Button to="/proof" variant="secondary">
            See Full Proof
          </Button>
        </div>
      </Section>

      {/* Final CTA */}
      <Section className="bg-gradient-to-br from-invert-navy to-invert-navy-muted text-white text-center">
        <FadeIn className="max-w-2xl mx-auto">
          <h2 className="font-interblack text-3xl lg:text-5xl mb-6">
            Never design another promotion again.
          </h2>
          <p className="text-xl text-slate-300 mb-10">
            The fastest way to communicate with customers. Every TV becomes an AI employee.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button to="/screenHostSignup" variant="white" size="lg">
              Get Started Free
            </Button>
            <Button to="/contactUs" variant="secondary" size="lg">
              Talk to Us
            </Button>
          </div>
        </FadeIn>
      </Section>
    </MarketingLayout>
  );
}
